import React from 'react';

interface SignatureAuditListProps {
  documentId: number;
  signatures: any[];
  onVerify?(signatureId: number): void;
  onDownloadSignedPdf?(): void;
  loading?: boolean;
}

export const SignatureAuditList: React.FC<SignatureAuditListProps> = ({
  documentId,
  signatures,
  onVerify,
  onDownloadSignedPdf,
  loading,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Signature history</h2>
        {onDownloadSignedPdf && (
          <button
            type="button"
            onClick={onDownloadSignedPdf}
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Download signed PDF
          </button>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-slate-500">Loading signatures…</p>
      ) : signatures.length === 0 ? (
        <p className="text-xs text-slate-500">No signatures yet for this document.</p>
      ) : (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900/40">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Signer</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Signed at</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300 hidden md:table-cell">
                  IP address
                </th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Status</th>
                {onVerify && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {signatures.map((sig) => {
                const signerPeople = sig?.signer?.people;
                const name =
                  signerPeople && (signerPeople.first_name || signerPeople.last_name)
                    ? `${signerPeople.first_name ?? ''} ${signerPeople.last_name ?? ''}`.trim()
                    : sig?.signer?.email ?? 'Unknown';
                const signedAt = sig?.signed_at ? new Date(sig.signed_at).toLocaleString() : '—';
                return (
                  <tr key={sig.id}>
                    <td className="px-3 py-2 text-slate-800 dark:text-slate-100">{name}</td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{signedAt}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400 hidden md:table-cell">
                      {sig.ip_address || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200 capitalize">{sig.status || 'signed'}</td>
                    {onVerify && (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => onVerify(sig.id)}
                          className="px-2 py-1 text-[11px] rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Verify
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        This list shows an audit trail for document #{documentId}, including signer, time, IP address, and status.
      </p>
    </div>
  );
};

